import {
  generateAmPmOptions,
  generateDateOptions,
  generateDayOptions,
  generateHourOptions,
  generateMinuteOptions,
  generateMonthOptions,
  generateYearOptions,
  parseDatePickerParts,
  pickerCurrentTime,
  PickerItem,
  pickerValidateAmPm,
  pickerValidateDate,
  pickerValidateDay,
  pickerValidateHour,
  pickerValidateMinute,
  pickerValidateMonth,
  pickerValidateYear,
  WheelPicker,
  type PickerItemValidCheck
} from "@9amhealth/shared";
import type { ZonedDateTime } from "@internationalized/date";
import { CalendarDate, getLocalTimeZone } from "@internationalized/date";
import type { FC } from "react";
import React from "react";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric"
});
const dateFormatterWithYear = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric"
});

function formatDateLabel(date: Date, includeYear: boolean): string {
  const formatter = includeYear ? dateFormatterWithYear : dateFormatter;
  return formatter
    .formatToParts(date)
    .map((p) => (p.type === "literal" && p.value === ", " ? " " : p.value))
    .join("");
}

function generateCustomDateOptions(daysBack: number, daysForward: number) {
  const today = pickerCurrentTime();
  const calendar = new CalendarDate(today.year, today.month, today.day);

  return Array.from({ length: daysBack + daysForward + 1 }, (_, i) => {
    let c = calendar.copy();
    c = c.add({ days: -daysBack + i });
    const isToday = i === daysBack;
    const date = c.toDate(getLocalTimeZone());
    return {
      value: c.toString(),
      label: isToday ? "Today" : formatDateLabel(date, c.year !== today.year)
    };
  });
}

interface Props {
  onDateSelected: (date: ZonedDateTime) => void;
  granularity?: "day" | "minute" | "ymd";
  initialDate?: ZonedDateTime;
  daysBack?: number;
  daysForward?: number;
  yearsBack?: number;
  yearsForward?: number;
  validateDate?: PickerItemValidCheck;
}

export const DateWheelPicker: FC<Props> = ({
  onDateSelected,
  granularity = "minute",
  initialDate,
  daysBack,
  daysForward,
  yearsBack,
  yearsForward,
  validateDate = pickerValidateDate
}) => {
  const now = pickerCurrentTime();
  const startAtDate = initialDate ?? now;
  const [selectedDate, setSelectedDate] =
    React.useState<ZonedDateTime>(startAtDate);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [val, setVal] = React.useState<{
    date?: string;
    hour?: string;
    minute?: string;
    amPm?: string;
    month?: string;
    day?: string;
    year?: string;
    dateOb?: Date;
  }>({});

  const dateValue = new CalendarDate(
    startAtDate.year,
    startAtDate.month,
    startAtDate.day
  ).toString();
  const minuteValue = startAtDate.minute.toString();
  const amPmValue = startAtDate.hour < 12 ? "AM" : "PM";
  const hourValue = `${startAtDate.hour % 12}`;
  const monthValue = startAtDate.month.toString();
  const dayValue = startAtDate.day.toString();
  const yearValue = startAtDate.year.toString();

  const dateItems = React.useMemo(
    () =>
      daysBack !== undefined || daysForward !== undefined
        ? generateCustomDateOptions(daysBack ?? 30, daysForward ?? 1)
        : generateDateOptions(),
    [daysBack, daysForward]
  );
  const hourItems = React.useMemo(() => generateHourOptions(), []);
  const minuteItems = React.useMemo(generateMinuteOptions, []);
  const amPmItems = React.useMemo(generateAmPmOptions, []);
  const monthItems = React.useMemo(() => generateMonthOptions(), []);
  const dayItems = React.useMemo(() => generateDayOptions(), []);
  const yearItems = React.useMemo(
    () => generateYearOptions(yearsBack ?? 120, yearsForward ?? 0),
    [yearsBack, yearsForward]
  );

  const pickerId = "datePicker";
  const handleChange = (data: typeof val) => {
    const parsed = parseDatePickerParts(data, pickerId);
    if (parsed) {
      setSelectedDate(parsed);
      setVal(data);
      onDateSelected(parsed);
    }
  };

  return (
    <WheelPicker
      onChange={handleChange}
      initialValue={selectedDate}
      pickerId={pickerId}
      parseValues={(values) => parseDatePickerParts(values, pickerId)}
    >
      {granularity !== "ymd" && (
        <PickerItem
          name="date"
          value={dateValue}
          perspective="left"
          alignContent={granularity === "minute" ? "right" : "center"}
          slides={dateItems}
          validCheck={validateDate}
        />
      )}
      {granularity === "ymd" && (
        <PickerItem
          name="month"
          value={monthValue}
          perspective="left"
          alignContent="right"
          slides={monthItems}
          validCheck={pickerValidateMonth}
        />
      )}
      {granularity === "ymd" && (
        <PickerItem
          name="day"
          value={dayValue}
          slides={dayItems}
          alignContent="center"
          validCheck={pickerValidateDay}
        />
      )}
      {granularity === "ymd" && (
        <PickerItem
          name="year"
          value={yearValue}
          slides={yearItems}
          alignContent="left"
          validCheck={pickerValidateYear}
        />
      )}
      {granularity === "minute" && (
        <PickerItem
          name="hour"
          slides={hourItems}
          value={hourValue}
          alignContent="right"
          validCheck={pickerValidateHour}
          loop
        />
      )}
      {granularity === "minute" && (
        <PickerItem
          name="minute"
          value={minuteValue}
          slides={minuteItems}
          alignContent="left"
          validCheck={pickerValidateMinute}
        />
      )}
      {granularity === "minute" && (
        <PickerItem
          name="amPm"
          value={amPmValue}
          slides={amPmItems}
          validCheck={pickerValidateAmPm}
          alignContent="left"
        />
      )}
    </WheelPicker>
  );
};
